import { useMemo } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { experimentalStyled as styled, alpha, useTheme } from "@mui/material";
import { Box, ButtonBase, Drawer, Typography } from "@mui/material";
import { LogoutOutlined } from "@mui/icons-material";

import Scrollbar from "../../components/Scrollbar";
import { MHidden } from "../../components/@material-extend";
import { EnvSwitcher, SidebarNav, ThemeModeToggle, UserAvatar } from "../../components/ui";
import { Hexagon } from "../../components/ui/Hexagon";
import { adminGroups, getPlatformGroups } from "./SidebarConfig";
import { useAuth } from "../../contexts/auth2";
import { IDecodedToken } from "../../services/userService";
import { isCleoEnabled, isPatientManagementEnabled } from "../../services/feature-flags.service";

// ----------------------------------------------------------------------

export const SIDEBAR_WIDTH = 270;

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: SIDEBAR_WIDTH,
  },
}));

const { REACT_APP_ACTUAL_VERSION } = process.env;

interface DashboardSidebarProps {
  isOpenSidebar: boolean;
  onCloseSidebar: () => void;
}

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }: DashboardSidebarProps) {
  const theme = useTheme();
  const { setUser, isAdm, user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const logout = () => {
    localStorage.removeItem("token");
    setUser({} as IDecodedToken);
    navigate("/login", { replace: true });
  };

  const groups = useMemo(() => {
    if (pathname.startsWith("/adm")) return adminGroups;
    return getPlatformGroups({
      cleoEnabled: isCleoEnabled(),
      patientsEnabled: isPatientManagementEnabled(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user]);

  const displayName = (user.email ?? "").split("@")[0] || "Minha conta";
  const planLabel = user.plan ?? "Gratuito";

  const isAdmArea = pathname.startsWith("/adm");

  const renderContent = (
    <Scrollbar
      sx={{
        height: "100vh",
        background: isAdmArea ? theme.palette.brand.sidebarAdmin : theme.palette.brand.sidebarApp,
        borderRight: isAdmArea
          ? `1px solid ${theme.palette.brand.sidebarBorder}`
          : `1px solid ${theme.palette.brand.sidebarBorder}`,
        "& .simplebar-content": {
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ p: "26px 22px 14px" }}>
        <Box component={RouterLink} to="/" sx={{ display: "flex", justifyContent: "center" }}>
          <img
            style={{ width: 140, height: 42, objectFit: "cover", objectPosition: "center", display: "block" }}
            src="/imgs/newLogoWhite.svg"
            alt="ProAtivos"
          />
        </Box>
      </Box>

      {isAdm() && (
        <Box sx={{ px: 2, pb: "14px" }}>
          <EnvSwitcher />
        </Box>
      )}

      <Box sx={{ position: "relative", overflow: "hidden" }}>
        <SidebarNav groups={groups} />
        {isAdmArea && (
          <Hexagon
            size={140}
            fill="none"
            stroke="#E9D9BC"
            strokeWidth={2}
            sx={{
              position: "absolute",
              right: -46,
              bottom: 150,
              opacity: 0.10,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ p: 2, mt: 1, borderTop: `1px solid ${alpha(theme.palette.common.white, 0.16)}` }}>
        <Box sx={{ mb: "6px" }}>
          <ThemeModeToggle onBrandSurface />
        </Box>

        <ButtonBase
          onClick={() => navigate("/dashboard/profile")}
          sx={{ width: "100%", justifyContent: "flex-start", gap: "11px", p: 1, borderRadius: "12px" }}
        >
          <UserAvatar variant="brand" label={displayName} />
          <Box sx={{ minWidth: 0, textAlign: "left" }}>
            <Typography noWrap sx={{ fontSize: "13.5px", fontWeight: 600, color: theme.palette.brand.navActiveText }}>
              {displayName}
            </Typography>
            <Typography noWrap sx={{ fontSize: "11px", color: theme.palette.brand.navItemText }}>
              {planLabel}
            </Typography>
          </Box>
        </ButtonBase>

        <ButtonBase
          onClick={logout}
          sx={{
            width: "100%",
            height: 42,
            mt: "10px",
            gap: 1,
            borderRadius: "11px",
            fontFamily: theme.typography.fontFamily,
            fontSize: "13px",
            fontWeight: 600,
            color: theme.palette.brand.navActiveText,
            backgroundColor: alpha(theme.palette.common.white, 0.16),
            "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.24) },
          }}
        >
          <LogoutOutlined sx={{ fontSize: 16 }} />
          Sair do sistema
        </ButtonBase>

        <Typography
          noWrap
          sx={{
            mt: "10px",
            px: 1,
            textAlign: "center",
            fontSize: "11px",
            color: theme.palette.brand.navGroupLabel,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          ProAtivos · {REACT_APP_ACTUAL_VERSION}
        </Typography>
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: `min(${SIDEBAR_WIDTH}px, calc(100vw - 24px))` } }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer open variant="persistent" PaperProps={{ sx: { width: SIDEBAR_WIDTH, border: "none" } }}>
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
